import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle } from '../../styles/Layout';
import { 
  FaGraduationCap, 
  FaChartLine, 
  FaUsers, 
  FaRocket,
  FaAward,
  FaExternalLinkAlt,
  FaCheckCircle,
  FaTrophy,
  FaHandshake,
  FaBuilding,
  FaGlobe,
  FaCog
} from 'react-icons/fa';

const ClientsPageContainer = styled.section`
  padding-top: 100px;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--secondary) 100%);
  color: white;
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 25% 25%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(255, 0, 128, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: var(--spacing-lg);
  font-weight: 900;
  line-height: 1.1;
  
  .primary-text {
    color: var(--text-primary);
  }
  
  .highlight-text {
    background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  margin-bottom: var(--spacing-xl);
  line-height: 1.7;
  color: var(--text-secondary);
`;

const ClientsSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 40%, rgba(0, 255, 136, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 70% 60%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
    z-index: 1;
  }
`;

const ClientsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const ClientCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  padding: var(--spacing-xl);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'} 0%, ${props => props.colorSecondary || 'var(--neon-magenta)'} 100%);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
    transform: translateY(-10px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.color || 'var(--neon-blue)'};
  }
`;

const ClientHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-lg);
`;

const ClientIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, ${props => props.background || 'var(--neon-blue)'} 0%, ${props => props.backgroundSecondary || 'var(--neon-blue)'}80 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: var(--spacing-lg);
  font-size: 2rem;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 10px 30px ${props => props.background || 'var(--neon-blue)'}40;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    border-radius: 20px;
  }
`;

const ClientInfo = styled.div`
  flex: 1;
`;

const ClientName = styled.h3`
  font-size: 1.6rem;
  margin-bottom: var(--spacing-sm);
  color: var(--text-primary);
  font-weight: 700;
  display: flex;
  align-items: center;
  
  a {
    color: inherit;
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      color: ${props => props.color || 'var(--neon-blue)'};
      text-shadow: 0 0 10px currentColor;
    }
  }
`;

const ClientWebsite = styled.a`
  color: ${props => props.color || 'var(--neon-blue)'};
  font-size: 0.9rem;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-weight: 500;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 5px;
    font-size: 0.8rem;
  }
  
  &:hover {
    text-shadow: 0 0 10px currentColor;
    transform: translateX(3px);
  }
`;

const ClientIndustry = styled.div`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'}20 0%, ${props => props.color || 'var(--neon-blue)'}10 100%);
  border: 1px solid ${props => props.color || 'var(--neon-blue)'}40;
  color: ${props => props.color || 'var(--neon-blue)'};
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: var(--spacing-lg);
  
  svg {
    margin-right: 5px;
    font-size: 0.9rem;
  }
`;

const SuccessStory = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const StoryTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  font-weight: 600;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 8px;
    color: var(--neon-green);
  }
`;

const StoryContent = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: var(--spacing-md);
`;

const ImpactMetrics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
`;

const MetricItem = styled.div`
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(0, 255, 136, 0.05) 100%);
  border: 1px solid rgba(0, 255, 136, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  color: var(--neon-green);
  font-weight: 500;
  
  svg {
    margin-right: 6px;
    font-size: 0.8rem;
  }
`;

const StatsSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 25% 25%, rgba(255, 0, 128, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: var(--spacing-xl);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    border-color: var(--neon-blue);
  }
  
  .number {
    font-size: 3rem;
    font-weight: 900;
    background: linear-gradient(135deg, var(--neon-green) 0%, var(--neon-blue) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    display: block;
    line-height: 1;
    margin-bottom: var(--spacing-sm);
  }
  
  .label {
    font-size: 1.1rem;
    color: var(--text-secondary);
    font-weight: 600;
  }
`;

const ClientsPage = () => {
  const clients = [
    {
      name: "BVK Group",
      website: "https://bvkgroup.org/",
      industry: "Education",
      icon: <FaGraduationCap />,
      color: "var(--neon-blue)",
      colorSecondary: "var(--neon-green)",
      background: "var(--neon-blue)",
      backgroundSecondary: "var(--neon-green)",
      successStory: {
        title: "Digital Transformation in Education Management",
        content: "BVK Group, a prestigious educational institution, transformed their administrative operations through EduNexus ERP implementation. The comprehensive solution streamlined their fee management, student administration, and academic planning processes. With automated workflows and real-time reporting, BVK Group achieved significant improvements in operational efficiency and student satisfaction.",
        metrics: [
          "90% Reduction in Manual Tasks",
          "Enhanced Fee Collection",
          "Improved Student Experience",
          "Streamlined Administration"
        ]
      }
    },
    {
      name: "Investoday (AIUI Solutions)",
      website: "https://aiui.co.in/",
      industry: "Financial Technology",
      icon: <FaChartLine />,
      color: "var(--neon-magenta)",
      colorSecondary: "var(--neon-purple)",
      background: "var(--neon-magenta)",
      backgroundSecondary: "var(--neon-purple)",
      successStory: {
        title: "Advanced Trading Platform Development",
        content: "AIUI Solutions partnered with us to develop Investoday, a sophisticated algorithmic trading platform. Our team delivered a comprehensive solution featuring real-time market data integration, advanced analytics, and AI-powered trading algorithms. The platform enables users to create, test, and deploy trading strategies with professional-grade tools and seamless broker API connectivity.",
        metrics: [
          "Real-time Data Processing",
          "AI-Powered Analytics",
          "Seamless API Integration",
          "Professional Trading Tools"
        ]
      }
    },
    {
      name: "Employee As Service (EaaS)",
      website: "#",
      industry: "Workforce Solutions",
      icon: <FaUsers />,
      color: "var(--neon-green)",
      colorSecondary: "var(--neon-yellow)",
      background: "var(--neon-green)",
      backgroundSecondary: "var(--neon-yellow)",
      successStory: {
        title: "Revolutionary Workforce Management Platform",
        content: "EaaS transformed workforce management through our innovative platform that connects companies with skilled professionals. The solution provides unprecedented workforce flexibility, enabling businesses to optimize costs and access specialized talent on-demand. Our comprehensive system includes AI-powered matching, project management tools, and seamless payment processing.",
        metrics: [
          "Enhanced Workforce Flexibility",
          "Cost Optimization Solutions",
          "AI-Powered Matching",
          "Streamlined Resource Management"
        ]
      }
    }
  ];

  const stats = [
    { number: "50+", label: "Successful Projects" },
    { number: "25+", label: "Happy Clients" },
    { number: "95%", label: "Client Satisfaction" },
    { number: "5+", label: "Years Experience" }
  ];

  return (
    <ClientsPageContainer>
      <HeroSection>
        <Container>
          <HeroContent>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <HeroTitle>
                <span className="primary-text">Our </span>
                <span className="highlight-text">Clients</span>
                <span className="primary-text"> & Their Success Stories</span>
              </HeroTitle>
              <HeroSubtitle>
                Discover how leading organizations across various industries have transformed their operations 
                and achieved remarkable growth through our innovative digital solutions and cutting-edge technology.
              </HeroSubtitle>
            </motion.div>
          </HeroContent>
        </Container>
      </HeroSection>

      <ClientsSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle style={{ color: 'white', textAlign: 'center' }}>
              Trusted by Industry Leaders
            </SectionTitle>
            <SectionSubtitle $dark style={{ textAlign: 'center' }}>
              From educational institutions to fintech companies, our clients achieve exceptional results 
              through our premium digital solutions and dedicated partnership approach.
            </SectionSubtitle>
          </motion.div>
          
          <ClientsGrid>
            {clients.map((client, index) => (
              <ClientCard
                key={index}
                color={client.color}
                colorSecondary={client.colorSecondary}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <ClientHeader>
                  <ClientIcon 
                    background={client.background}
                    backgroundSecondary={client.backgroundSecondary}
                  >
                    {client.icon}
                  </ClientIcon>
                  <ClientInfo>
                    <ClientName color={client.color}>
                      <a href={client.website} target="_blank" rel="noopener noreferrer">
                        {client.name}
                      </a>
                    </ClientName>
                    {client.website !== "#" && (
                      <ClientWebsite 
                        href={client.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        color={client.color}
                      >
                        Visit Website <FaExternalLinkAlt />
                      </ClientWebsite>
                    )}
                  </ClientInfo>
                </ClientHeader>
                
                <ClientIndustry color={client.color}>
                  <FaBuilding />
                  {client.industry}
                </ClientIndustry>
                
                <SuccessStory>
                  <StoryTitle>
                    <FaTrophy />
                    {client.successStory.title}
                  </StoryTitle>
                  <StoryContent>
                    {client.successStory.content}
                  </StoryContent>
                </SuccessStory>
                
                <ImpactMetrics>
                  {client.successStory.metrics.map((metric, metricIndex) => (
                    <MetricItem key={metricIndex}>
                      <FaCheckCircle />
                      {metric}
                    </MetricItem>
                  ))}
                </ImpactMetrics>
              </ClientCard>
            ))}
          </ClientsGrid>
        </Container>
      </ClientsSection>

      <StatsSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle style={{ color: 'white', textAlign: 'center' }}>
              Our Track Record
            </SectionTitle>
            <SectionSubtitle $dark style={{ textAlign: 'center' }}>
              Numbers that speak to our commitment to excellence and client success across all projects and partnerships.
            </SectionSubtitle>
          </motion.div>
          
          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <span className="number">{stat.number}</span>
                <span className="label">{stat.label}</span>
              </StatCard>
            ))}
          </StatsGrid>
        </Container>
      </StatsSection>
    </ClientsPageContainer>
  );
};

export default ClientsPage;